import { mergeStyleSets } from '@fluentui/react';

/**
 * Shared look for the product query composer surfaces: the global palette's passthrough mode
 * and the search page's inline ProductSearchBox render the same chips, suggestion pill, and
 * token field. Host-specific spacing (the palette's modal margins vs. the page's full-width
 * row) is applied by the host via inline style overrides.
 */
export const productQueryClasses = mergeStyleSets({
    pillRow: { display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 14px 8px' },
    pill: {
        fontSize: 11,
        color: '#5a7523',
        border: '1px solid #a8bf78',
        borderRadius: 12,
        padding: '2px 10px',
        backgroundColor: '#f3f7e8',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        selectors: { '&:hover': { backgroundColor: '#e6efd2' } },
    },
    pillField: { fontWeight: 600 },
    pillOn: {
        backgroundColor: '#789c2f',
        borderColor: '#789c2f',
        color: '#fff',
        selectors: { '&:hover': { backgroundColor: '#5a7523' } },
    },
    // The completion is one enclosed pill trailing the typed text: a gray tab-key mini-chip,
    // the suggested value, and the field it fills. Clicking it accepts, same as Tab.
    suggestionPill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        marginLeft: 24,
        border: '1px solid #d2d0ce',
        borderRadius: 12,
        // Same vertical metrics as the committed-criteria chips (.pill: 2px padding, 11px text)
        // so the suggestion sits flush with them in the token row. Left padding matches the
        // vertical so the TAB mini-chip is inset evenly on its three enclosed sides.
        padding: '2px 10px 2px 3px',
        backgroundColor: '#faf9f8',
        color: '#605e5c',
        fontSize: 11,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        overflow: 'hidden',
        selectors: { '&:hover': { backgroundColor: '#f3f2f1' } },
    },
    suggestionTabChip: {
        backgroundColor: '#e1dfdd',
        borderRadius: 8,
        padding: '0 6px',
        fontSize: 10,
        color: '#484644',
        flexShrink: 0,
    },
    suggestionValue: { overflow: 'hidden', textOverflow: 'ellipsis' },
    suggestionField: { color: '#a19f9d', fontSize: 11, flexShrink: 0 },
    // Invisible text-width mirror so the input can shrink to its content (proportional font,
    // so ch-units will not do) and the suggestion pill can trail the actual caret position.
    textMirror: {
        position: 'absolute',
        visibility: 'hidden',
        whiteSpace: 'pre',
        fontSize: 14,
        fontFamily: 'inherit',
        pointerEvents: 'none',
    },
    filterGroupRow: { display: 'flex', alignItems: 'baseline', gap: 8, paddingTop: 6 },
    filterGroupLabel: {
        flexShrink: 0,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: '#605e5c',
        width: 74,
    },
    filterOptions: { display: 'flex', flexWrap: 'wrap', gap: 5 },
    // The passthrough token field: chips inline with a bare input that grows to fill the
    // remaining space.
    tokenField: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 5,
        minHeight: 36,
        padding: '4px 8px',
        border: '1px solid #8a8886',
        borderRadius: 2,
        cursor: 'text',
        backgroundColor: '#fff',
        selectors: { '&:focus-within': { borderColor: '#789c2f' } },
    },
    sigilGlyph: { color: '#789c2f', fontWeight: 700, fontSize: 14 },
    tokenInput: {
        flexGrow: 1,
        minWidth: 120,
        border: 'none',
        outline: 'none',
        fontSize: 14,
        fontFamily: 'inherit',
        padding: '4px 0',
        backgroundColor: 'transparent',
    },
});
